// DashboardSidebar and DashboardHeader are now in DashboardLayout
import { NextScrapingProgress } from '@/components/NextScrapingProgress';
import { CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Lightbulb, Plus, Loader2, Instagram, Sparkles } from 'lucide-react';
import { useInspirationSources } from '@/hooks/useInspirationSources';
import { useInspirationQuality } from '@/hooks/useInspirationQuality';
import { InspirationQualityCard } from '@/components/InspirationQualityCard';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { useState, useCallback, lazy, Suspense } from 'react';
import { extractInstagramUsername, validateInstagramInput } from '@/utils/instagramUsername';
import { useProfileVerification, ProfileAvatar, VerifiableProfileInput } from '@/components/onboarding/ProfileVerification';
import { useTierLimits } from '@/hooks/useTierLimits';
import { InspirationSkeleton } from '@/components/skeletons/InspirationSkeleton';

const InteractiveGlobe = lazy(() =>
  import('@/components/ui/interactive-globe').catch(() => {
    window.location.reload();
    return new Promise(() => {}); // never resolves — page is reloading
  })
);

const Inspiration = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [sourceToDelete, setSourceToDelete] = useState<{ id: string; username: string } | null>(null);
  const [addInputs, setAddInputs] = useState<string[]>(['']);
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});
  const [isAdding, setIsAdding] = useState(false);

  const { verifications, verifyProfile, clearVerification, removeAndReindex } = useProfileVerification(user?.id || null);
  const tierLimits = useTierLimits();
  const maxSources = tierLimits.limits.maxInspirationSources;

  const {
    sources,
    isLoading,
    error,
    addSource,
    removeSource,
    getSourceLimits,
    isGenerating,
    generationProgress
  } = useInspirationSources(user?.id, maxSources);

  const { stats: qualityStats } = useInspirationQuality(user?.id || null);

  const limits = getSourceLimits();
  const filledInputs = addInputs.filter(v => v.trim() !== '').length;
  const totalCount = limits.currentSources + filledInputs;
  const slotLimitReached = limits.currentSources >= maxSources;
  const monthlyLimitReached = tierLimits.remainingInspirationAdds <= 0;
  const isLimitReached = slotLimitReached || monthlyLimitReached;
  const canAddMoreInputs = totalCount < maxSources && !isLimitReached;

  const handleInputChange = useCallback((index: number, value: string) => {
    const lowercaseValue = value.toLowerCase();
    setAddInputs(prev => {
      const next = [...prev];
      next[index] = lowercaseValue;
      return next;
    });
    clearVerification(index);
    const error = validateInstagramInput(lowercaseValue);
    setValidationErrors(prev => {
      const next = { ...prev };
      if (error) next[index] = error;
      else delete next[index];
      return next;
    });
  }, [clearVerification]);

  const handleRemoveInput = useCallback((index: number) => {
    setAddInputs(prev => prev.filter((_, i) => i !== index));
    removeAndReindex(index);
    setValidationErrors(prev => {
      const next: Record<number, string> = {};
      for (const [key, val] of Object.entries(prev)) {
        const idx = Number(key);
        if (idx < index) next[idx] = val;
        else if (idx > index) next[idx - 1] = val;
      }
      return next;
    });
  }, [removeAndReindex]);

  const handleAddInput = useCallback(() => {
    if (canAddMoreInputs) {
      setAddInputs(prev => [...prev, '']);
    }
  }, [canAddMoreInputs]);

  const handleVerify = useCallback((index: number, input: string) => {
    const username = extractInstagramUsername(input);
    if (!username) return;
    // Check duplicates against existing sources
    const isDuplicate = sources.some(s => s.username.toLowerCase() === username.toLowerCase());
    if (isDuplicate) {
      setValidationErrors(prev => ({ ...prev, [index]: `@${username} jest już na liście` }));
      return;
    }
    verifyProfile(index, input);
  }, [sources, verifyProfile]);

  const handleAddVerifiedSources = async () => {
    const verifiedUsernames = addInputs
      .map((val, idx) => {
        const v = verifications[idx];
        if (v?.status === 'success' && !v.is_private && !v.is_blocked) {
          return extractInstagramUsername(val);
        }
        return null;
      })
      .filter(Boolean) as string[];

    if (verifiedUsernames.length === 0) return;

    setIsAdding(true);
    try {
      for (const username of verifiedUsernames) {
        await addSource(username);
      }
      setAddInputs(['']);
      // Clear all verifications
      for (const key of Object.keys(verifications)) {
        clearVerification(Number(key));
      }
      await tierLimits.refresh();
      toast({
        title: 'Źródła dodane',
        description: `Dodano ${verifiedUsernames.length} ${verifiedUsernames.length === 1 ? 'profil' : 'profile'}`,
      });
    } catch (err) {
      toast({
        title: t('common.error'),
        description: err instanceof Error ? err.message : t('inspiration.failedToAdd'),
        variant: "destructive",
      });
    } finally {
      setIsAdding(false);
    }
  };

  const verifiedCount = addInputs.filter((_, i) => {
    const v = verifications[i];
    return v?.status === 'success' && !v.is_private && !v.is_blocked;
  }).length;

  const handleRemoveSource = async () => {
    if (!sourceToDelete) return;
    
    try {
      await removeSource(sourceToDelete.id);
      toast({
        title: t('inspiration.sourceRemoved'),
        description: t('inspiration.sourceRemovedDesc', { username: sourceToDelete.username }),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('inspiration.failedToRemove'),
        variant: "destructive",
      });
    } finally {
      setSourceToDelete(null);
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'default';
      case 'processing': return 'secondary';
      case 'failed': return 'destructive';
      default: return 'outline';
    }
  };

  if (isLoading) {
    return <InspirationSkeleton />;
  }

  return (
    <>
      <main className="flex-1 p-3 md:p-6 pb-16 flex justify-center overflow-y-auto">
        <div className="w-full max-w-[1200px] space-y-4 md:space-y-6 pb-16">

          {/* Next Scraping Progress */}
          <NextScrapingProgress />

          {/* Progress Indicator */}
          {isGenerating && generationProgress.total > 0 && (
            <div className="glass-card rounded-2xl p-6 border border-primary/30 bg-primary/5">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <span className="font-semibold">{t('inspiration.scrapingContent')}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {t('inspiration.completedCount', { completed: generationProgress.completed.toString(), total: generationProgress.total.toString() })}
                  </span>
                </div>
                <Progress 
                  value={(generationProgress.completed / generationProgress.total) * 100} 
                  className="h-2"
                />
                {generationProgress.currentSource && (
                  <p className="text-sm text-muted-foreground">
                    {t('inspiration.processing', { username: generationProgress.currentSource.username })}
                  </p>
                )}
              </div>
            </div>
          )}


          {/* Add Source */}
          <div className="glass-card rounded-2xl overflow-hidden border border-border/50">
            <div className="flex flex-col md:flex-row items-start gap-8 md:gap-12 p-8 md:p-12">
              {/* Globe */}
              <div className="flex-shrink-0 self-center md:self-start md:mt-1">
                <Suspense fallback={<div className="w-[120px] h-[120px] rounded-full bg-muted/30 animate-pulse" />}>
                  <InteractiveGlobe size={120} />
                </Suspense>
              </div>

              {/* Content */}
              <div className="flex-1 w-full">
                <div className="max-w-md space-y-1">
                  {isLimitReached ? (
                    <>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Sparkles className="h-4 w-4 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold">
                          {monthlyLimitReached && !slotLimitReached
                            ? 'Limit dodań w tym miesiącu wyczerpany'
                            : 'Wszystkie miejsca zajęte!'}
                        </h3>
                      </div>
                      <p className="text-muted-foreground text-sm">
                        {monthlyLimitReached && !slotLimitReached
                          ? `Wykorzystano ${tierLimits.changeCounts.inspirationAdds}/${tierLimits.limits.monthlyInspirationAdds} dodań w tym okresie. Odnowi się automatycznie.`
                          : `Masz już ${maxSources} źródeł inspiracji — to maksimum dla Twojego planu. Usuń jedno, aby dodać nowe.`}
                      </p>
                    </>
                  ) : (
                    <div className="space-y-3">
                      {addInputs.map((value, index) => (
                        <VerifiableProfileInput
                          key={index}
                          index={index}
                          value={value}
                          onChange={(idx, val) => handleInputChange(idx, val)}
                          onRemove={handleRemoveInput}
                          onVerify={(idx, input) => handleVerify(idx, input)}
                          onEnterAdd={handleAddInput}
                          verification={verifications[index]}
                          validationError={validationErrors[index]}
                          canRemove={addInputs.length > 1}
                          icon={<Instagram className="h-4 w-4" />}
                          totalCount={addInputs.length}
                        />
                      ))}

                      <div className="flex items-center gap-2 pt-1 pl-10">
                        {canAddMoreInputs && (
                          <Button variant="outline" size="sm" onClick={handleAddInput} className="text-xs">
                            <Plus className="mr-1 h-3 w-3" /> Dodaj kolejny
                          </Button>
                        )}
                        {verifiedCount > 0 && (
                          <Button onClick={handleAddVerifiedSources} disabled={isAdding} size="sm">
                            {isAdding ? (
                              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Dodawanie...</>
                            ) : (
                              <>Dodaj {verifiedCount} {verifiedCount === 1 ? 'źródło' : verifiedCount < 5 ? 'źródła' : 'źródeł'}</>
                            )}
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground pl-10">
                        {totalCount} z {maxSources} źródeł · {tierLimits.remainingInspirationAdds} dodań pozostało w tym miesiącu
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Sources List */}
          {sources.length > 0 && (
            <h2 className="text-lg md:text-xl font-semibold text-foreground">
              Profile, które analizujesz:
            </h2>
          )}
          
          <div className="grid gap-3 md:gap-4">
            {sources.map((source) => (
              <InspirationQualityCard
                key={source.id}
                source={source}
                qualityStats={qualityStats.find(s => s.inspiration_source_id === source.id)}
                onDelete={setSourceToDelete}
              />
            ))}
          </div>

          {sources.length === 0 && (
            <div className="glass-card rounded-2xl p-12 text-center border border-border/50">
              <Lightbulb className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('inspiration.noSources')}</h3>
              <p className="text-muted-foreground">
                {t('inspiration.noSourcesDesc')}
              </p>
            </div>
          )}

          {error && (
            <div className="glass-card rounded-2xl p-4 border border-destructive/50 bg-destructive/5">
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}

          {/* Bottom spacer for mobile navbar clearance */}
          <div className="h-16" aria-hidden="true" />
          </div>
        </main>

      <AlertDialog open={!!sourceToDelete} onOpenChange={(open) => !open && setSourceToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('inspiration.removeSource')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('inspiration.removeSourceDesc', { username: sourceToDelete?.username || '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveSource}>
              {t('common.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Inspiration;